import { inject, Injectable } from '@angular/core';
import { BaseStoreFacade } from '@city/data-access';
import { FeatureFlagService } from '@core/feature';
import { AuthService } from '@spartacus/core';
import { combineLatest, map, Observable } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { CurrentProfileService } from './current-profile.service';
import { LoyaltyProgramService } from './loyalty-program.service';

// Сервис для проверки доступности бонусной системы
@Injectable({ providedIn: 'root' })
export class BonusSystemFeatureAvailableService {
  private loyaltyProgramService = inject(LoyaltyProgramService);
  private currentProfileService = inject(CurrentProfileService);

  constructor(
    private baseStoreFacade: BaseStoreFacade,
    private authService: AuthService,
    private featureFlagService: FeatureFlagService,
  ) {}

  isFeatureAvailable(): Observable<boolean | undefined> {
    return combineLatest({
      isUserLoggedIn: this.authService.isUserLoggedIn(),
      isFeatureAvailableForBaseStore: this.isFeatureAvailableForBaseStore(),
    }).pipe(
      map(({ isUserLoggedIn, isFeatureAvailableForBaseStore }) => {
        return isUserLoggedIn && isFeatureAvailableForBaseStore;
      }),
    );
  }

  canRegister(): Observable<boolean | undefined> {
    return this.featureFlagService.isEnabled('loyalty.canRegister').pipe(distinctUntilChanged());
  }

  useBonusLimit(): Observable<boolean | undefined> {
    return this.featureFlagService.isEnabled('loyalty.useBonusLimit').pipe(distinctUntilChanged());
  }

  isFeatureAvailableForUser(): Observable<boolean | undefined> {
    return combineLatest({
      canRegister: this.canRegister(),
      isFeatureAvailableForBaseStore: this.isFeatureAvailableForBaseStore(),
      isUserLoggedIn: this.authService.isUserLoggedIn(),
      isMember: this.loyaltyProgramService.getIsUserMemberOfLoyaltyProgram(),
      isForeman: this.currentProfileService.isForeman(),
    }).pipe(
      map(({ canRegister, isFeatureAvailableForBaseStore, isUserLoggedIn, isMember, isForeman }) => {
        if (isForeman) {
          return false;
        }

        if (!isFeatureAvailableForBaseStore) {
          return false;
        }

        if (canRegister) {
          return true;
        } else {
          if (isUserLoggedIn && isMember) {
            return true;
          }
        }

        return false;
      }),
    );
  }

  isFeatureAvailableForBaseStore(): Observable<boolean | undefined> {
    return this.baseStoreFacade.getActiveBaseStore().pipe(map((store) => store?.bonusSystemEnabled ?? false));
  }
}
